import { Layout } from '@common/data-models/service/layout'
import { Record } from "@common/data-models/records/record";
import { CollectionRecord } from "@common/data-models/records/collection-record";
import { UserRecord } from "@common/data-models/records/user-record";
import { ObjectTypes } from "@common/enums/object-types";
import { SearchResults } from "@common/data-models/search-results/search-results";
import { UsersResults } from "@common/data-models/search-results/users-result";
import { PlanningViewsResults } from "@common/data-models/navigation/planning-views-results";
import { PublicationChannelsResults } from "@common/data-models/publication-channels/publication-channels-results";
import { AppService } from '@suite/app.service';

import { IRealtimeUpdatesProvider } from '@common/interfaces/i-realtime-updates-provider';
import { EnpsComponent } from '@common/components/base/enps-component';

import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ViewStateRecord } from "@common/data-models/records/viewstate-record";


export class DataStore {
    public static realtimeUpdatesProviders: { [id: string]: IRealtimeUpdatesProvider } = {};
    public static addRealtimeUpdatesProvider(provider: IRealtimeUpdatesProvider, name?: string) {
        DataStore.realtimeUpdatesProviders[name ? name : "enps"] = provider;
    }

    public static appService: AppService;

    private static getKey(guid: string, providerName?: string): string {
        let prefix = providerName ? providerName : "enps";
        return prefix + "_" + guid;
    }

    private static getProviderName(key: string): string {
        return key.split("_")[0];
    }


    public static disposeRecordSimple(rec: Record) {
        if (rec instanceof CollectionRecord) {
            let children = rec.getChildren();

            for (let child of children) {
                delete DataStore.records[DataStore.getKey(child.guid)];
            }
        }

        delete DataStore.records[DataStore.getKey(rec.guid)];
    }

    public static disposeRecord(rec: Record, disposeChildren?: boolean, providerName?: string) {
        if (disposeChildren && (rec instanceof CollectionRecord)) {
            let childGuids: { [id: string]: boolean } = {};
            let children = rec.getChildren();
            for (let child of children) {
                childGuids[child.guid] = true;
                for (let key in DataStore._searchResults) {
                    DataStore._searchResults[key].remove(child);
                }
            }

            for (let guid in childGuids) {
                if (DataStore.records[DataStore.getKey(guid, providerName)]) {
                    if (DataStore.records[DataStore.getKey(guid, providerName)].parentChangedSub &&
                        !DataStore.records[DataStore.getKey(guid, providerName)].parentChangedSub.closed) {
                        DataStore.records[DataStore.getKey(guid, providerName)].parentChangedSub.unsubscribe();
                    }
                    DataStore.records[DataStore.getKey(guid, providerName)].parentRecord = null;
                }
                delete DataStore.records[DataStore.getKey(guid, providerName)];
            }
        }
        // Remove all references in all groupings
        for (let key in DataStore._searchResults) {
            DataStore._searchResults[key].remove(rec);
        }
        for (let key in DataStore._viewStateResults) {
            DataStore._viewStateResults[key].remove(<ViewStateRecord>rec);
        }

        if (rec.parentRecord) {
            rec.parentRecord = null;
        }

        if (rec.links) {
            rec.links = null;
        }

        if (DataStore.records[DataStore.getKey(rec.guid, providerName)]) {
            if (DataStore.records[DataStore.getKey(rec.guid, providerName)].parentRecord) {
                DataStore.records[DataStore.getKey(rec.guid, providerName)].parentRecord = null;
            }

            if (DataStore.records[DataStore.getKey(rec.guid, providerName)].links) {
                DataStore.records[DataStore.getKey(rec.guid, providerName)].links = null;
            }
        }

        delete DataStore.records[DataStore.getKey(rec.guid, providerName)];
    }

    // Components
    public static componentByReferalId: { [id: string]: EnpsComponent } = {};

    // RTU subscriptions
    public static rtuSubscriptions: { [id: string]: boolean } = {};

    // Data manipulation routines
    private static records: { [id: string]: Record } = {};
    public static byGuid<T extends Record>(guid: string, referal: string, providerName?: string): T {
        let key = DataStore.getKey(guid, providerName);
        if (DataStore.records[key]) {
            return <T><any>DataStore.records[key];
        } else {
            return null;
        }
    }

    private static async subscribeToUpdatesForId(subscriptionId: string, providerName: string) {
        if (!DataStore.rtuSubscriptions[subscriptionId]) {
            DataStore.rtuSubscriptions[subscriptionId] = true;
            await DataStore.realtimeUpdatesProviders[providerName].subscribe(subscriptionId);
        }
    }

    public static async subscribeForPathUpdates(database: string, path: string, providerName?: string) {
        let subscriptionId = DataStore.realtimeUpdatesProviders[providerName ? providerName : "enps"].getSubscriptionIdForPath(database, path);
        this.subscribeToUpdatesForId(subscriptionId, providerName ? providerName : "enps");
    }

    private static async subscribeForRecordUpdates(record: Record, providerName?: string) {
        if (record && record.database) {
            let subscriptionId = DataStore.realtimeUpdatesProviders[providerName ? providerName : "enps"].getSubscriptionId(record);
            this.subscribeToUpdatesForId(subscriptionId, providerName ? providerName : "enps");
            if (record.isCollection) {
                // Subscribe for collection itself
                let collectionSubscriptionId = subscriptionId + "\\R_" + record.guid;
                this.subscribeToUpdatesForId(collectionSubscriptionId, providerName ? providerName : "enps");
            }
            if (record.path.groupId) {
                let trashSubscriptionId = "D=" + record.database + "&P=P_" + record.path.groupId + "\\T";
                this.subscribeToUpdatesForId(trashSubscriptionId, providerName ? providerName : "enps");
            }
        }
    }

    public static async set(record: Record, providerName?: string) {
        let guid = record.guid;
        let storeKey = DataStore.getKey(guid, providerName);
        if (DataStore.records[storeKey]) {
            let fields: { [id: string]: any } = {};
            for (let field of record.keys) {
                fields[field] = record.get(field);
            }
            DataStore.records[storeKey].setFields(fields);
        } else {
            DataStore.records[storeKey] = record;
        }

        if (record.links && record.links.length > 0) {
            DataStore.records[storeKey].links = record.links;
        }

        await DataStore.subscribeForRecordUpdates(DataStore.records[storeKey], providerName);
    }

    public static async setCollection(collection: CollectionRecord, hasChildren: boolean, providerName?: string) {
        let collectionKey = DataStore.getKey(collection.guid, providerName);
        // TODO: orphans detach/dispose
        await DataStore.set(collection, providerName);

        let dataStoreCollection = <CollectionRecord>DataStore.records[collectionKey];
        if (collection.layout && dataStoreCollection && (dataStoreCollection.layout != collection.layout)) {
            // Update layout
            if (!dataStoreCollection.layout) dataStoreCollection.layout = new Layout();
            dataStoreCollection.layout.update(collection.layout);
        }
        if (collection.sequence && collection.sequence.length > 0 && collection.getChildren().length == 0) {
            return;
        }
        if ((collection.getChildren().length > 0) || hasChildren) {
            let dataStoreChildren: Array<Record> = [];
            let children = collection.getChildren();
            for (let child of children) {
                if (child.isCollection) {
                    await DataStore.setCollection(<CollectionRecord>child, false, providerName); // TODO: Nested collections support?
                } else {
                    await DataStore.set(child, providerName);
                }
                let dataStoreChild = DataStore.records[DataStore.getKey(child.guid, providerName)];
                dataStoreChildren.push(dataStoreChild);
            }

            if (dataStoreCollection) dataStoreCollection.setChildren(dataStoreChildren);
        }

        await DataStore.subscribeForRecordUpdates(DataStore.records[collectionKey], providerName);
    }

    private static async applyCollectionDiff(collection: CollectionRecord, providerName?: string) {
        let collectionKey = DataStore.getKey(collection.guid, providerName);
        let record = <CollectionRecord>DataStore.records[collectionKey];
        if (record) {
            for (let prop of collection.keys) {
                if (collection.get(prop)) record.set(prop, collection.get(prop));
            }

            let children = collection.getChildren();
            for (let child of children) {
                let childRecord = DataStore.records[DataStore.getKey(child.guid, providerName)];
                if (childRecord) {
                    for (let prop of child.keys) {
                        if (child.get(prop)) childRecord.set(prop, child.get(prop));
                    }
                    if (!record.hasChildWithGuid(child.guid)) {
                        record.addChildren(DataStore.byGuid<Record>(child.guid, ""));
                    }
                }
            }

            for (let removeGuid of collection.guidsToBeRemoved) {
                record.removeChildByGuid(removeGuid);
            }

            // Sort
            record.sortChildren((a: Record, b: Record) => {
                return a.scriptOrder - b.scriptOrder;
            });
        }
    }

    public static disposeSearchResults() {
        this.componentByReferalId = {};
        for (let key in DataStore._searchResults) {
            let results = DataStore._searchResults[key].getAllResults();
            for (let result of results) {
                DataStore.disposeRecordSimple(result);
            }
            DataStore._searchResults[key].dispose();
        }
    }

    public static disposePlanningViewsResults() {
        for (let view of DataStore.planningViewsResults.views) {
            DataStore.disposeRecord(view, false);
        }
        for (let search of DataStore.planningViewsResults.searches) {
            DataStore.disposeRecord(search, false);
        }
        for (let slideshow of DataStore.planningViewsResults.slideshows) {
            DataStore.disposeRecord(slideshow, false);
        }
        DataStore.planningViewsResults.dispose();
    }

    private static _searchResults: { [id: string]: SearchResults } = {};
    public static registerSearchResults(key: string, obj: SearchResults) {
        DataStore._searchResults[key] = obj;
    }
    public static getSearchResults<T extends SearchResults>(key: string): T {
        return <T>DataStore._searchResults[key];
    }
    public static get searchResultsKeys(): Array<string> {
        return Object.keys(DataStore._searchResults);
    }

    private static _viewStateResults: { [id: string]: PlanningViewsResults } = {
        "playbook": new PlanningViewsResults(),
        "discover": new PlanningViewsResults(),
        "live": new PlanningViewsResults(),
        "cast": new PlanningViewsResults(),
        "journal": new PlanningViewsResults()
    };
    public static registerViewStateResults(key: string, obj: PlanningViewsResults) {
        DataStore._viewStateResults[key] = obj;
    }
    public static getViewStateResults<T extends PlanningViewsResults>(key: string): T {
        return <T>DataStore._viewStateResults[key];
    }
    public static get viewStateResultsKeys(): Array<string> {
        return Object.keys(DataStore._viewStateResults);
    }
    public static getSingleViewStates(key: string): Array<ViewStateRecord> {
        let viewstates: Array<ViewStateRecord> = [];
        if (DataStore.getViewStateResults(key)) {
            for (let vs of DataStore.getViewStateResults(key).views) {
                viewstates.push(vs);
            }
            for (let vs of DataStore.getViewStateResults(key).searches) {
                viewstates.push(vs);
            }
        }

        viewstates = viewstates.sort((a: ViewStateRecord, b: ViewStateRecord) => {
            if (a.title < b.title) return -1;
            if (a.title > b.title) return 1;
            return 0;
        });
        return viewstates;
    }

    public static usersResults = new UsersResults();

    public static planningViewsResults = new PlanningViewsResults();

    public static publicationChannelsResults = new PublicationChannelsResults();

    public static markers = new Array<any>();

    private static _rtuListeners: { [id: string]: (rec: Record) => void } = {};

    public static registerRTUListener(key: string, f: (rec: Record) => void) {
        DataStore._rtuListeners[key] = f;
    }

    public static unregisterRTUListener(key: string) {
        delete DataStore._rtuListeners[key];
    }

    public static rtuHandler(rec: Record, providerName?: string) {
        DataStore.updateMapMarker(rec);
        if (rec instanceof CollectionRecord) {
            if (rec.isCollectionDiff) {
                DataStore.applyCollectionDiff(rec, providerName);
            } else {

                DataStore.setCollection(<CollectionRecord>rec, true, providerName);
            }
            let dataStoreRec = DataStore.byGuid<CollectionRecord>(rec.guid, "");
            //DataStore.searchResults.rtuHandler(dataStoreRec);
        } else {
            if (rec.type == ObjectTypes.ASSIGNMENT || rec.type == ObjectTypes.PUBLICATION) {
                if (rec.path.isWasteBinPath) {
                    // Assignment or publication deleted
                    let existingRecord = DataStore.byGuid<Record>(rec.guid, "");
                    if (existingRecord && !existingRecord.path.isWasteBinPath) {
                        let collection = DataStore.byGuid<CollectionRecord>(existingRecord.path.parentCollectionGuid, "");
                        if (collection) {
                            if (rec.parentChangedSub && !rec.parentChangedSub.closed) rec.parentChangedSub.unsubscribe();
                            rec.parentRecord = null;
                            collection.removeChildByGuid(rec.guid);
                        }
                    }
                    DataStore.set(<Record>rec);
                } else {
                    // Added or updated
                    DataStore.set(<Record>rec);
                    let collection = DataStore.byGuid<CollectionRecord>(rec.path.parentCollectionGuid, "");
                    if (collection) {
                        if (!collection.hasChildWithGuid(rec.guid)) {
                            // Need to add to collection
                            collection.addChildren(DataStore.byGuid<Record>(rec.guid, ""));
                        }
                    }
                }
            } else {
                DataStore.set(<Record>rec);
                let dataStoreRec = DataStore.byGuid<Record>(rec.guid, "");
                //DataStore.searchResults.rtuHandler(dataStoreRec);
            }
        }
        for (let key in DataStore._rtuListeners) {
            DataStore._rtuListeners[key](DataStore.byGuid<Record>(rec.guid, ""));
        }
    }

    // TODO: move to map
    private static async updateMapMarker(rec: Record) {
        let guid: string = rec.guid;
        let lat: number = 0;
        let lng: number = 0;
        let found: boolean = false;
        if (rec["data"] && rec["data"][PSTFieldNames.PSTTopicAddress]) {
            // topic or assignment
            lat = Number(rec.get("PSTTopicAddress").lat);
            lng = Number(rec.get("PSTTopicAddress").long);
            found = true;
        } else if (rec instanceof UserRecord) {
            // User Record Resource
            let user: UserRecord = rec as UserRecord;
            if (user.locations && user.locations.locations.length > 0) {
                lat = user.locations.getLastKnownLocation().latitude;
                lng = user.locations.getLastKnownLocation().longitude;
                found = true;
                // we also need to update the user resources list in appService
                if (DataStore.appService.userResources == null) {
                    DataStore.appService.userResources = [];
                }
                DataStore.appService.userResources.push(rec);
                if (!DataStore.markers.find(m => m.guid == rec.guid)) {
                    DataStore.appService.navigationService.currentListView.addUserToMap(rec);
                }
            }
        }
        if (found) {
            for (let marker of DataStore.markers) {
                if (marker.guid == guid) {
                    marker.latitude = lat;
                    marker.longitude = lng;
                    break;
                }
            }
        }
    }

    public static dump() {
        console.log("DataStore records: " + Object.keys(DataStore.records).length);
        //console.log(DataStore.records.length);
    }
}

